/**
 * URL de redirecionamento do formulario (FORM-02, FORM-50) — funcoes puras.
 *
 * A pagina hospedada /f/:slug roda na MESMA origem do ERP, com a sessao do
 * usuario no localStorage. Um `javascript:` gravado como redirecionamento
 * executava ali e levava o token de quem enviasse o formulario. Por isso so
 * passa URL absoluta http(s) com host — a mesma regra de
 * `formularios.services.url_http_segura` no backend, que tambem recusa o valor
 * ao gravar. A tela confere antes de `location.assign` mesmo assim: defesa em
 * profundidade contra valor antigo ou resposta adulterada.
 *
 * Sao duas chaves no `config`, cada uma editada numa tela:
 *   - `redirect_url`: para onde a pagina hospedada leva o visitante (Editor);
 *   - `ingestao_redirect_url`: pagina de obrigado da landing externa (Publicar).
 */

export const TAMANHO_MAXIMO_URL_REDIRECT = 500;
export const MENSAGEM_URL_REDIRECT_INVALIDA = "Informe a URL completa, começando com https://";
export const CHAVE_REDIRECT_PAGINA_HOSPEDADA = "redirect_url";
export const CHAVE_REDIRECT_INGESTAO = "ingestao_redirect_url";

const PROTOCOLOS_ACEITOS = ["http:", "https:"];
const COMECA_COM_HTTP = /^https?:\/\//i;
// "www.site.com.br/obrigado", "site.com:8080": nome com ponto, sem esquema.
const PARECE_DOMINIO = /^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?([/?#]|$)/i;
const ESPACO = /\s/;
const PRIMEIRO_CARACTERE_IMPRIMIVEL = 0x20;
const CARACTERE_DELETE = 0x7f;

// Espaco no meio ou caractere de controle: o navegador "conserta" a URL de um
// jeito que o backend recusa, entao os dois lados recusam antes.
function temCaractereProibido(texto) {
  return [...texto].some((caractere) => {
    const codigo = caractere.codePointAt(0);
    return (
      codigo < PRIMEIRO_CARACTERE_IMPRIMIVEL
      || codigo === CARACTERE_DELETE
      || ESPACO.test(caractere)
    );
  });
}

/**
 * A URL sem espacos nas pontas quando e' http(s) absoluta com host; senao "".
 *
 * O teste do prefixo `http(s)://` vem antes do `new URL`: o navegador aceita
 * `https:site.com` e o backend nao. Sem esquema ("www.site.com") o navegador
 * trataria como caminho relativo dentro do ERP.
 */
export function urlHttpSegura(url) {
  if (typeof url !== "string") return "";
  const texto = url.trim();
  if (!texto || [...texto].length > TAMANHO_MAXIMO_URL_REDIRECT) return "";
  if (temCaractereProibido(texto) || !COMECA_COM_HTTP.test(texto)) return "";
  let partes;
  try {
    partes = new URL(texto);
  } catch {
    return "";
  }
  if (!PROTOCOLOS_ACEITOS.includes(partes.protocol) || !partes.hostname) return "";
  return texto;
}

/**
 * Para o `onBlur` do campo: quem digita "www.site.com/obrigado" quis dizer
 * https. Qualquer outro texto volta como veio (so sem espacos nas pontas),
 * para o erro inline apontar o que esta errado.
 */
export function completarEsquemaHttps(valor) {
  const texto = String(valor ?? "").trim();
  if (!texto || COMECA_COM_HTTP.test(texto) || !PARECE_DOMINIO.test(texto)) return texto;
  return `https://${texto}`;
}

/** "" quando o valor pode ser gravado (vazio limpa); senao a mensagem do backend. */
export function erroUrlRedirect(valor) {
  const texto = String(valor ?? "").trim();
  if (!texto || urlHttpSegura(texto)) return "";
  return MENSAGEM_URL_REDIRECT_INVALIDA;
}
